import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import Image from 'antd/lib/image'
import ImageLineIcon from 'remixicon-react/ImageLineIcon'
import PlayCircleLineIcon from 'remixicon-react/PlayCircleLineIcon'
import XArrowIcon from '@UI/XArrowIcon'
import { getBase64 } from '@utils/helpers/fileHelpers'
import image from '@assets/images/default-event-image.jpeg'
import styles from './index.scss'
import CloseCircleLineIcon from 'remixicon-react/CloseCircleLineIcon'
import RestartLineIcon from 'remixicon-react/RestartLineIcon'
import SkipForwardLineIcon from 'remixicon-react/SkipForwardLineIcon'

interface IProps {
    background: string
    steps: {
        image: any
        _id: string
        title: string
        description?: string
        titleLink: string
        status: string
    }[]
    showSkipButton?: boolean
    showRestartButton?: boolean
    handleClosePreview?: () => void
}

const ImageItem = props => {
    const { step } = props
    const [src, setSrc] = useState(image)
    const [videoSrc, setVideoSrc] = useState('')
    const [activeMedia, setActiveMedia] = useState<'image' | 'video'>('image')
    const getSrc = async (key = 'image') => {
        if(step?.image && typeof step?.image == "string") {
            return step?.image;
        } else {
            const file = step[key]?.[0]
            if (file?.url) {
                return file?.url
            } else if (file?.originFileObj) {
                return await Promise.resolve(getBase64(file?.originFileObj))
            } else {
                setActiveMedia('video')
                return image
            }
        }
    }
    const getVideoSrc = async () => {
        if(typeof step?.video == "string") {
            return step?.video;
        } else {
            const file = step.video?.[0]
            if (file?.url) {
                return file?.url
            } else if (file?.originFileObj) {
                return await Promise.resolve(getBase64(file?.originFileObj))
            } else {
                return ''
            }
        }
    }

    useEffect(() => {
        getSrc().then(value => {
            setSrc(value)
        })
        getVideoSrc().then(value => {
            setVideoSrc(value)
        })
    }, [step])

    return (
        <div className={styles['step-image']}>
            <div className={cx(styles['step-image-container'], { [styles['active-video']]: activeMedia === 'video' })}>
                <Image key={step._id} preview={false} src={src} fallback={image} alt="" />
                <video
                    controls
                    style={{
                        insetInlineStart: activeMedia === 'video' ? 0 : '100%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                >
                    <source src={videoSrc} type="video/mp4" />
                </video>
            </div>
            {(step.image?.length > 0 || (step?.image && typeof step?.image == "string")) && (step.video?.length > 0 || step.video_link) && (
                <div className={styles['step-image-nav']}>
                    <span
                        className={cx({ [styles['active-media']]: activeMedia === 'image' })}
                        onClick={() => setActiveMedia('image')}
                    >
                        <ImageLineIcon size={24} />
                    </span>
                    <span
                        className={cx({ [styles['active-media']]: activeMedia === 'video' })}
                        onClick={() => setActiveMedia('video')}
                    >
                        <PlayCircleLineIcon size={24} />
                    </span>
                </div>
            )}
        </div>
    )
}

const Preview = (props: IProps) => {
    const { background, steps, showSkipButton, showRestartButton, handleClosePreview } = props
    const [activeStep, setActiveStep] = useState(0)

    const enabledSteps = steps?.filter(step => step?.status == "enabled");

    return (
        <div className={styles['container']} style={{ background }}>
            <div className={styles['icons-wrapper']}>
                {showSkipButton && (
                    <SkipForwardLineIcon
                        size={28}
                        onClick={handleClosePreview}
                    />
                )}
                {showRestartButton && (
                    <RestartLineIcon
                        size={28}
                        onClick={() => setActiveStep(0)}
                    />
                )}
                <CloseCircleLineIcon
                    size={28}
                    onClick={handleClosePreview}
                />
            </div>
            <div className={styles['wrapper']}>
                {enabledSteps?.map((step, index) => (
                    <div
                        className={styles.step}
                        style={{ insetInlineStart: ((index - activeStep) * 100).toString() + '%' }}
                        key={step._id}
                    >
                        <div className={styles['step-container']} onClick={() => setActiveStep(index)}>
                            <ImageItem step={step} />
                            <div className={styles['step-info']}>
                                <a href={step.titleLink} target="_blank" rel="noopener noreferrer">
                                    <span>{step.title}</span>
                                </a>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: step?.description || ''
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className={styles.navigator}>
                <XArrowIcon
                    size={16}
                    onClick={() => setActiveStep(state => (state - 1 < 0 ? 0 : state - 1))}
                    className={cx({ [styles.disabled]: activeStep === 0 })}
                />
                {enabledSteps?.map((step, index) => (
                    <div
                        key={step._id}
                        className={cx(styles['navigator-item'], {
                            [styles['active']]: index === activeStep
                        })}
                        onClick={() => setActiveStep(index)}
                    >
                        <span />
                        <div>{step.title || ''}</div>
                    </div>
                ))}
                <XArrowIcon
                    reverse
                    className={cx({ [styles.disabled]: activeStep + 1 === enabledSteps?.length })}
                    size={16}
                    onClick={() => setActiveStep(state => (state + 1 < enabledSteps?.length ? state + 1 : state))}
                />
            </div>
        </div>
    )
}

export default Preview
